import RegionRepository from '@api/repositories/region-repository'
import { IRegion } from '@app/interfaces/organization/region'
import FormList from '@app/pages/reference-book-page-details/components/form-list/form-list'
import ModalFooter from '@app/shared/modal-footer/modal-footer'
import { openNotificationWithIcon } from '@app/shared/notification/notification'
import { createRegion, updateRegion } from '@app/store/actions/regions-actions'
import { Button, Form, Input, InputNumber, Modal, Spin, Switch } from 'antd'
import { AxiosResponse } from 'axios'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import './region-form.less'

interface props {
  region?: IRegion
  edit?: boolean
}

const RegionForm: React.FC<props> = ({ edit, region }) => {
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false)
  const [visible, setVisible] = useState<boolean>(false)
  const [form] = Form.useForm()
  const [t] = useTranslation()
  const showModal = () => setVisible(true)
  const hideModal = () => {
    setVisible(false)
    form.resetFields()
  }
  const dispatch = useDispatch()

  const onFinish = async (values: any) => {
    const languages = values.data.filter(
      (language) => language && language.name,
    )
    const newValues: IRegion = {
      id: values.id,
      soato: values.soato,
      sorting: values.sorting,
      status: values.status,
      data: languages,
    }

    setConfirmLoading(true)
    const regionSubmitApi = region
      ? RegionRepository.update
      : RegionRepository.create

    await regionSubmitApi(newValues)
      .then((response: AxiosResponse<IRegion>) => {
        if (region && response.status === 200) {
          setConfirmLoading(false)
          dispatch(updateRegion(response.data))
          form.resetFields()
          openNotificationWithIcon('success', t('success.updated'))
        }
        if (!region && response.status === 201) {
          setConfirmLoading(false)
          dispatch(createRegion(response.data))
          form.resetFields()
          openNotificationWithIcon('success', t('success.created'))
        }
        hideModal()
      })
      .catch(() => {
        setConfirmLoading(false)
        openNotificationWithIcon('error', t('errors.error'))
      })
  }

  return (
    <React.Fragment>
      <Button onClick={showModal} type={'primary'}>
        {edit ? (
          <i className="far fa-edit"> {t('general.edit')} </i>
        ) : (
          t('general.add')
        )}
      </Button>

      <Modal
        open={visible}
        title={edit ? t('region.editRegion') : t('region.addRegion')}
        confirmLoading={confirmLoading}
        onCancel={hideModal}
        footer={[
          <React.Fragment key={1}>
            <ModalFooter
              confirm={confirmLoading}
              edit={edit}
              onCancel={hideModal}
              onSubmit={form.submit}
            />
          </React.Fragment>,
        ]}
      >
        <Spin spinning={confirmLoading}>
          <Form
            form={form}
            onFinish={onFinish}
            initialValues={{
              id: region?.id || null,
              soato: region?.soato || 0,
              sorting: region?.sorting || 0,
              status: edit ? region?.status : true,
            }}
            layout={'vertical'}
          >
            <Form.Item hidden={true} name={'id'}>
              <Input />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  type: 'number',
                  message: t('errors.numberMessage'),
                },
              ]}
              label={'soato'}
              name={'soato'}
            >
              <InputNumber
                disabled={confirmLoading}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('errors.requiredMessage'),
                },
              ]}
              name={'status'}
              label={t('general.status')}
              valuePropName="checked"
            >
              <Switch
                disabled={confirmLoading}
                defaultChecked={region?.status === true}
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  type: 'number',
                  min: -2147483648,
                  max: 2147483648,
                  message: t('errors.minPriorityMessage'),
                },
              ]}
              label={t('general.sort')}
              name={'sorting'}
            >
              <InputNumber
                disabled={confirmLoading}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <FormList confirmLoading={confirmLoading} object={region} />
          </Form>
        </Spin>
      </Modal>
    </React.Fragment>
  )
}

export default RegionForm
